import { AsyncPipe, DatePipe, Location } from '@angular/common';
import { Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatCard, MatCardModule, MatCardTitle } from '@angular/material/card';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatList } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';

import { LogEntry } from 'src/app/models/logEntry';
import { AuditService } from 'src/app/services/audit.service';
import { UserService } from 'src/app/services/user.service';

import { Audit } from '../../../models/audit/audit';
import { Recommendation } from '../../../models/recommendation/recommendation';
import { UserNamePipe } from 'src/app/pipes/user-name.pipe';
import { AzureIdStatusPipe } from 'src/app/pipes/azure-id-status.pipe';

@Component({
  selector: 'app-recommendation-hidden-fields',
  templateUrl: './recommendation-hidden-fields.component.html',
  styleUrls: ['./recommendation-hidden-fields.component.scss'],
  providers: [DatePipe],
  imports: [
    MatCard,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardTitle,
    MatList,
    MatFormField,
    MatInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    UserNamePipe,
    AzureIdStatusPipe,
    AsyncPipe
  ]
})
export class RecommendationHiddenFieldsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private auditService = inject(AuditService);
  private location = inject(Location);

  dialog = inject(MatDialog);

  private _adapter = inject<DateAdapter<Date>>(DateAdapter);

  userService = inject(UserService);

  readonly recommendations = input.required<Recommendation>();
  readonly form = input.required<UntypedFormGroup>();
  readonly save = output();

  currentTab = 0;
  datePipe = new DatePipe('en_EU');

  // First column of the big list
  bigLists = [
    'Username',
    'Sequence number',
    'Status',
    'Short Text neutral',
    'Short text aus Dec"1"',
    'Short text aus Dec"2"',
    'Short Text Adjourned',
    'Prolongation, Date',
    'Display Prolo Fields',
    'Audit Definite:',
    'Special Audit',
    'Basic Defs:',
    'Date actual Deadline',
    'Prolo Decision, Date other Deadline',
    'Decision, Save:',
    'Names list',
    'Names list',
    'Name of last modifier',
    'Date of last modification'
  ];

  // Second column of the big list
  firstValues: string[] = [];
  // Third column of the big list
  secondValues: string[] = [];

  // First column of the last list
  smallLists = [
    'Authors Reco Assessor',
    'Authors Reco Auditor',
    'Readers defined Responsibilities',
    'Readers LU',
    'Readers BU'
  ];
  // Second column of the last list
  smallFirstValues: string[] = [];
  // Last column of the last list
  smallSecondValues = [
    'Write access for Auditor / Coordinator (1 Person)',
    'Write access for Auditor ("Team" Role)',
    'Read access for defined (LU) Responsibles of Recommendation',
    'Read access for LU role',
    'Read access for BG role'
  ];

  finalLists: LogEntry[] = [];
  finalSmallLists: LogEntry[] = [];

  audit!: Audit;
  displayName = '';
  error!: string;

  options!: UntypedFormControl;

  arrayModActions!: string[];
  arrayModDates!: string[];
  arrayModStatuses!: string[];

  isNewRecommendation = false;
  validation(): boolean {
    return this.form().valid;
  }

  ngOnInit() {
    this.displayName = this.userService.userProfile().displayName ?? '';
    if (this.route.snapshot.params.audit) {
      this.isNewRecommendation = true;
      if (window.history.state.audit) {
        this.audit = window.history.state.audit;
      } else {
        this.auditService.getDataById(this.route.snapshot.params?.audit).subscribe({
          next: (auditEx) => (this.audit = auditEx)
        });
      }
    }
    this.getAllValues();
    this.dateFormat();
    this.currentTab = this.route.snapshot.params?.tab;
  }

  dateFormat() {
    this._adapter.setLocale('de');
  }

  goBack() {
    this.location.back();
  }

  isProlongationStatus(prolo_status: string) {
    return this.recommendations()?.prolongationStatus === prolo_status;
  }

  isOverdue(time: string): boolean {
    const today = new Date();
    const deadline = new Date(time);
    return deadline >= today;
  }

  async getAllValues() {
    // ALL values of a specific element
    this.buildHiddenFieldsArray();
    this.mergeArray(this.bigLists, this.firstValues, this.secondValues, this.finalLists);
    this.mergeArray(this.smallLists, this.smallFirstValues, this.smallSecondValues, this.finalSmallLists);
  }

  isNew(): boolean {
    return this.isNewRecommendation;
  }

  buildHiddenFieldsArray() {
    const recommendations = this.recommendations();
    if (!recommendations.prolongationStatus) {
      recommendations.prolongationStatus = ' ';
    }
    this.firstValues[0] = this.displayName;
    this.firstValues[1] = recommendations.sequenceNumber1 + '.' + recommendations.sequenceNumber2;
    this.firstValues[2] = recommendations.status;
    this.firstValues[3] = recommendations.adjournedReason;
    this.firstValues[4] = recommendations.shortText1;
    this.firstValues[5] = recommendations.prolongationToAssessor;
    this.firstValues[6] = recommendations.adjournedReason;
    this.firstValues[7] =
      recommendations.prolongationStatus +
      ' --- ' +
      this.datePipe.transform(recommendations.prolongationDeadline, 'dd.MM.yyyy') +
      ' --- ';
    this.firstValues[9] = recommendations.auditDefinition;
    this.firstValues[10] = recommendations.isSpecialAudit.toString();
    this.firstValues[11] = recommendations.auditDefinition;
    this.firstValues[12] = this.datePipe.transform(recommendations.initialDeadline, 'dd.MM.yyyy') ?? '';
    this.firstValues[13] =
      recommendations.prolongationDecision +
      ', ' +
      this.datePipe.transform(recommendations.prolongationOtherDate, 'dd.MM.yyyy');
    this.firstValues[14] = recommendations.decision;
    this.firstValues[15] = recommendations.myAttention?.toString();
    this.firstValues[16] = recommendations.keypersons?.toString();
    this.firstValues[17] = recommendations.modifiedBy;
    this.firstValues[18] = recommendations.modifiedAt;

    this.secondValues[14] = 'for use in views "MyAtt..."';
    this.secondValues[15] = 'for use in view "OMKeypersons"';

    this.smallFirstValues[0] = recommendations.assessmentCoordinatorName || recommendations.assessmentCoordinator;
    this.smallFirstValues[1] = recommendations.authorsBinGen;
    if (recommendations.readersResponsible) {
      this.smallFirstValues[2] = recommendations.readersResponsible.toString();
    }
    this.smallFirstValues[3] = recommendations.legalUnit;
    this.smallFirstValues[4] = recommendations.department;
  }

  mergeArray(
    arr1: string[],
    arr2: string[],
    arr3: string[],
    finalArray: { text: string; value1: string; value2: string }[]
  ) {
    arr1.forEach((element, i) => {
      finalArray.push({ text: element, value1: arr2[i], value2: arr3[i] });
    });
  }
}
